import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CPE } from '../types/cpe';

interface CPEBoardCardProps {
  /** The CPE to display */
  cpe: CPE;
  /** Zone name lookup (id → name) */
  zoneMap: Record<string, string>;
  /** Called when the card is clicked */
  onClick: (cpe: CPE) => void;
}

/**
 * Draggable Jira-style board card for a single CPE.
 * Displays name, zone chip, power, and next action with overdue highlight.
 */
export const CPEBoardCard: React.FC<CPEBoardCardProps> = ({ cpe, zoneMap, onClick }) => {
  const { t } = useTranslation();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: cpe.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const today = new Date().toISOString().split('T')[0];
  const isOverdue =
    cpe.next_action_id != null &&
    cpe.next_action_due_date != null &&
    cpe.next_action_due_date < today;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onClick(cpe)}
      sx={{
        mb: 1,
        borderLeft: '3px solid',
        borderLeftColor: isOverdue ? 'error.main' : cpe.is_waldevar_pp ? '#1d388d' : 'divider',
        bgcolor: isOverdue ? 'rgba(198, 40, 40, 0.08)' : undefined,
        '&:hover': { boxShadow: 4 },
        userSelect: 'none',
      }}
      elevation={isDragging ? 6 : 1}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Name */}
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{ mb: 1, lineHeight: 1.3 }}
          >
            {cpe.name}
          </Typography>

          {/* Zone + Power row */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: cpe.next_action_id ? 1 : 0 }}>
            {cpe.zone_id && zoneMap[cpe.zone_id] && (
              <Chip
                label={zoneMap[cpe.zone_id]}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
            {cpe.power != null && (
              <Chip
                label={`${cpe.power} MW`}
                size="small"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
          </Box>

          {/* Next Action */}
          {cpe.next_action_id && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {cpe.nextAction && (
                <Chip
                  label={cpe.nextAction.name}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
              {cpe.next_action_due_date && (
                <Chip
                  label={
                    isOverdue
                      ? `${t('nextAction.overdue', 'Overdue')} — ${cpe.next_action_due_date}`
                      : cpe.next_action_due_date
                  }
                  size="small"
                  color={isOverdue ? 'error' : 'default'}
                  variant={isOverdue ? 'filled' : 'outlined'}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Box>
          )}
        </CardContent>
    </Card>
  );
};

export default CPEBoardCard;
